import { useMemo, type HTMLAttributes, type ReactNode } from 'react';
import { useColumnManager, useHeaderMenu } from '../../components/AppDataGrid';
import { useSeqGridView } from './useSeqGridView';

/** One column of a {@link useMiniGrid} table. */
export interface MiniCol<T> {
  key: string;
  label: string;
  /** Preferred width in px. Never narrower than the caption needs (see captionWidth). */
  width?: number;
  /** Displayed text — used for the Excel-style filter values and the default sort. */
  text: (row: T) => string;
  /** Raw value to sort by (dates / numbers); defaults to `text`. */
  sortValue?: (row: T) => unknown;
  /** Custom cell content; defaults to `text`. */
  cell?: (row: T) => ReactNode;
  /** Extra class on the <th> and <td> (e.g. `num`). */
  className?: string;
}

export interface MiniGridOptions<T> {
  rowKey: (row: T) => string;
  /** Fixed leading column (row checkbox / add-remove button). Not hideable, sortable or filterable. */
  lead?: { width: number; head?: ReactNode; cell: (row: T) => ReactNode };
  /** Fixed trailing column (row actions). Not hideable, sortable or filterable. */
  tail?: { width: number; head?: ReactNode; cell: (row: T) => ReactNode };
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement>;
  /** Message shown when there are no rows (after filtering). */
  empty: string;
  loading?: boolean;
}

export interface MiniGrid<T> {
  /** The <table> plus its portaled header menu / column chooser. */
  table: ReactNode;
  /** Total table width in px — lets a popup widen itself to fit its grid. */
  width: number;
  /** Rows as shown (filtered + sorted). */
  viewRows: T[];
}

// Header text is 12px bold (qcBatch.css) ≈ 7.2px a character; the sort arrow, the
// filter chevron and the cell padding need ≈ 58px more. A column is never set
// narrower than this, so a caption never collapses behind its icons.
const captionWidth = (label: string) => Math.ceil(label.length * 7.2) + 58;

/**
 * Hide / Show All / Column Chooser (header right-click), click-to-sort, the shared
 * Excel-style filter and drag-resize for the small hand-rolled grids of the QC Batch /
 * Prep Batch forms (pickers, Others tab, Copy From Previous Batch, Project Notes).
 * Built only from the shared pieces the sequence grid uses (useColumnManager ·
 * useHeaderMenu · useSeqGridView — CLAUDE.md rule 8). Runtime-only: the layout
 * resets when the popup / form reopens, like XAF. View-only — callers keep their own
 * row arrays for commit logic.
 */
export function useMiniGrid<T>(columns: MiniCol<T>[], rows: readonly T[], opts: MiniGridOptions<T>): MiniGrid<T> {
  const cm = useColumnManager(columns, c => c.key, c => c.label);
  const byKey = useMemo(() => new Map(columns.map(c => [c.key, c])), [columns]);
  const view = useSeqGridView(rows, {
    text: (r, k) => byKey.get(k)?.text(r) ?? '',
    sortValue: (r, k) => { const c = byKey.get(k); return c ? (c.sortValue ?? c.text)(r) : ''; },
    keys: cm.visibleColumns.map(c => c.key),
  });
  const menu = useHeaderMenu(view.menuSort, cm.columnMenu);

  const widthOf = (c: MiniCol<T>) => cm.colWidth(c.key) ?? Math.max(c.width ?? 0, captionWidth(c.label));
  const cols = cm.visibleColumns;
  const leadW = opts.lead?.width ?? 0;
  const tailW = opts.tail?.width ?? 0;
  const width = leadW + tailW + cols.reduce((s, c) => s + widthOf(c), 0);
  const span = cols.length + (opts.lead ? 1 : 0) + (opts.tail ? 1 : 0);

  const table = (
    <>
      <table className="mgrid" style={{ width, minWidth: '100%' }}>
        <colgroup>
          {opts.lead && <col style={{ width: leadW }} />}
          {cols.map(c => <col key={c.key} style={{ width: widthOf(c) }} />)}
          {opts.tail && <col style={{ width: tailW }} />}
        </colgroup>
        <thead><tr>
          {opts.lead && <th className="cbcol">{opts.lead.head}</th>}
          {cols.map(c => (
            <th key={c.key} className={c.className}
                style={{ position: 'relative' }}
                onClick={() => view.onSort(c.key)}
                onContextMenu={e => menu.openHeaderMenu(e, { field: c.key, label: c.label })}>
              {view.header(c.key, c.label)}
              <span className="adg-resize" onMouseDown={e => cm.startResize(c.key, e)} onClick={e => e.stopPropagation()} />
            </th>
          ))}
          {opts.tail && <th>{opts.tail.head}</th>}
        </tr></thead>
        <tbody>
          {opts.loading ? (
            <tr><td colSpan={span} className="onodata">Loading…</td></tr>
          ) : view.viewRows.length === 0 ? (
            <tr><td colSpan={span} className="onodata">{opts.empty}</td></tr>
          ) : view.viewRows.map(r => (
            <tr key={opts.rowKey(r)} {...opts.rowProps?.(r)}>
              {opts.lead && <td className="cbcol">{opts.lead.cell(r)}</td>}
              {cols.map(c => <td key={c.key} className={c.className}>{c.cell ? c.cell(r) : c.text(r)}</td>)}
              {opts.tail && <td>{opts.tail.cell(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {menu.headerMenu}
      {cm.chooser}
    </>
  );
  return { table, width, viewRows: view.viewRows };
}

/**
 * Popup width that fits `tableWidth` in each of `panels` side-by-side panels (plus the
 * .pb padding, panel borders and the .dual gap), never below `base`. The .pickmodal
 * max-width (97vw) still caps it; a grid wider than that scrolls inside its panel.
 */
export function popupWidthFor(base: number, tableWidth: number, panels = 1): number {
  const need = panels * (tableWidth + 2) + (panels - 1) * 14 + 34;
  return Math.max(base, need);
}
